import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  HowToReg as RsvpIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  BarChart as AnalyticsIcon,
  Visibility as TrackingIcon,
  CardGiftcard as GiftIcon,
  QrCode2 as CheckInIcon,
  Tune as CustomizeIcon,
  Speed as SpeedIcon,
  Lock as PrivacyIcon,
  AttachMoney as PricingIcon,
  ArrowForward as ArrowIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Webhook as WebhookIcon,
  Palette as BrandingIcon,
  Groups as GroupsIcon,
  HourglassBottom as WaitlistIcon,
} from '@mui/icons-material';

export default function LandingPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ bgcolor: '#FAFAFA', overflow: 'hidden' }}>
      {/* ─── NAV BAR ─── */}
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 1.5 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                letterSpacing: '-0.02em',
                cursor: 'pointer',
              }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              TimeToRSVP
            </Typography>

            {!isMobile && (
              <Stack direction="row" spacing={3} alignItems="center">
                <NavLink href="#features">Features</NavLink>
                <NavLink href="#how-it-works">How It Works</NavLink>
                <NavLink href="#pricing">Pricing</NavLink>
                <NavLink href="#faq">FAQ</NavLink>
              </Stack>
            )}

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{ color: 'text.primary' }}
              >
                Log In
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
                disableElevation
              >
                Get Started Free
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ─── HERO ─── */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 14 },
          background: 'linear-gradient(170deg, #5C6BC0 0%, #3949AB 40%, #283593 100%)',
          color: '#fff',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -120,
            right: -80,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -60,
            left: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255,112,67,0.12)',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.2rem', sm: '3rem', md: '3.75rem' },
              lineHeight: 1.15,
              mb: 3,
              letterSpacing: '-0.03em',
            }}
          >
            Send beautiful invites.{' '}
            <Box component="span" sx={{ color: '#FF9A76' }}>
              Know who's coming.
            </Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              fontSize: { xs: '1.05rem', md: '1.3rem' },
              lineHeight: 1.6,
              maxWidth: 620,
              mx: 'auto',
              mb: 5,
              opacity: 0.9,
            }}
          >
            TimeToRSVP is the no-monthly-fee event invite platform for birthdays, holidays,
            dinners, and every gathering that matters. Create an event, send stunning
            emails, and track every RSVP — starting at just $2.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              disableElevation
              sx={{
                bgcolor: '#FF7043',
                color: '#fff',
                px: 5,
                py: 1.8,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2,
                '&:hover': { bgcolor: '#E64A19' },
              }}
            >
              Create Your First Event
              <ArrowIcon sx={{ ml: 1 }} />
            </Button>
            <Button
              variant="outlined"
              size="large"
              href="#how-it-works"
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.5)',
                px: 4,
                py: 1.8,
                fontSize: '1.05rem',
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              See How It Works
            </Button>
          </Stack>

          <Typography sx={{ mt: 4, opacity: 0.65, fontSize: '0.9rem' }}>
            No credit card required to get started
          </Typography>
        </Container>
      </Box>

      {/* ─── TRUST BAR ─── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            divider={
              <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
            }
            spacing={0}
            sx={{ py: { xs: 3, md: 4 } }}
          >
            <TrustStat value="$2" label="per event — no monthly fees" />
            <TrustStat value="30 sec" label="to send your first invite" />
            <TrustStat value="100%" label="RSVP visibility for hosts" />
          </Stack>
        </Container>
      </Box>

      {/* ─── HOW IT WORKS ─── */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <SectionHeader
            overline="Simple by Design"
            title="Three steps to a fully managed event"
            subtitle="No apps to install, no accounts for your guests. Just a beautiful invite and a clear RSVP."
          />

          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              {
                step: '1',
                title: 'Create your event',
                desc: 'Pick a template, add your event details, set the date, and build your guest list — or import from CSV.',
                icon: <CustomizeIcon sx={{ fontSize: 32 }} />,
              },
              {
                step: '2',
                title: 'Send stunning invites',
                desc: 'Guests get a professionally designed email with one-click RSVP. No sign-up required on their end.',
                icon: <EmailIcon sx={{ fontSize: 32 }} />,
              },
              {
                step: '3',
                title: 'Track every response',
                desc: 'See who opened, clicked, and RSVP\'d in real time. Send reminders and updates with a single click.',
                icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
              },
            ].map((item) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.step}>
                <Card
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    '&:hover': {
                      boxShadow: '0 8px 32px rgba(92,107,192,0.12)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="overline"
                      sx={{ color: 'primary.main', fontWeight: 700, mb: 0.5, display: 'block' }}
                    >
                      Step {item.step}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {item.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── FEATURES ─── */}
      <Box
        id="features"
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <SectionHeader
            overline="Everything You Need"
            title="Powerful features, simple experience"
            subtitle="From beautiful emails to real-time analytics — everything to make your event a hit."
          />

          <Grid container spacing={3} sx={{ mt: 2 }}>
            {features.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Stack direction="row" spacing={2} sx={{ p: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      minWidth: 48,
                      borderRadius: 1.5,
                      bgcolor: f.color || 'primary.light',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {f.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {f.desc}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── EVENT TYPES SHOWCASE ─── */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#FAFAFA' }}>
        <Container maxWidth="lg">
          <SectionHeader
            overline="For Every Occasion"
            title="One platform for all your gatherings"
            subtitle="Birthday parties, holiday dinners, team offsites, baby showers — if people are coming, TimeToRSVP has you covered."
          />
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            sx={{ gap: 2, mt: 4 }}
          >
            {eventTypes.map((e) => (
              <Box
                key={e}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 10,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: 'rgba(92,107,192,0.04)',
                  },
                }}
              >
                {e}
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ─── PRICING ─── */}
      <Box id="pricing" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <SectionHeader
            overline="Simple Pricing"
            title="Pay per event. No subscriptions."
            subtitle="Host a party for $2 — not $20/month. TimeToRSVP grows with your guest list, not your credit card statement."
          />

          <Grid
            container
            spacing={4}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            {/* Personal Plan */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card
                sx={{
                  height: '100%',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    color: '#fff',
                    textAlign: 'center',
                    py: 0.75,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  Most Popular
                </Box>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Personal
                  </Typography>
                  <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
                      $2
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>/ event</Typography>
                  </Stack>
                  <Typography sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
                    Up to 15 guests included
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={1.5} sx={{ textAlign: 'left', mb: 4 }}>
                    <PricingItem text="Beautiful email templates" />
                    <PricingItem text="One-click guest RSVP" />
                    <PricingItem text="Real-time RSVP dashboard" />
                    <PricingItem text="Calendar invite generation" />
                    <PricingItem text="Co-host collaboration" />
                    <PricingItem text="Email tracking & analytics" />
                    <PricingItem text="5 emails per guest included" />
                    <PricingItem text="+$1 per additional 5 guests" />
                  </Stack>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    disableElevation
                    onClick={() => navigate('/register')}
                    sx={{ py: 1.5, fontSize: '1rem' }}
                  >
                    Start for $2
                  </Button>
                  <Typography sx={{ mt: 1.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                    No account needed until you create an event
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Pro Plan */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 4, textAlign: 'center', pt: 5.75 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Pro
                  </Typography>
                  <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: 'secondary.main' }}>
                      $2
                    </Typography>
                    <Typography sx={{ color: 'text.secondary' }}>/ event + extras</Typography>
                  </Stack>
                  <Typography sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
                    For hosts who want more control
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={1.5} sx={{ textAlign: 'left', mb: 4 }}>
                    <PricingItem text="Everything in Personal" bold />
                    <PricingItem text="Custom branding & colors" />
                    <PricingItem text="Image library uploads" />
                    <PricingItem text="API access & webhooks" />
                    <PricingItem text="Embeddable RSVP widget" />
                    <PricingItem text="Advanced guest analytics" />
                    <PricingItem text="Priority support" />
                    <PricingItem text="Waitlist management" />
                  </Stack>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={() => navigate('/register')}
                    sx={{ py: 1.5, fontSize: '1rem' }}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Business Plan */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 4, textAlign: 'center', pt: 5.75 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Business
                  </Typography>
                  <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5}>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary' }}>
                      Custom
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
                    Monthly license for organizations
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Stack spacing={1.5} sx={{ textAlign: 'left', mb: 4 }}>
                    <PricingItem text="Everything in Pro" bold />
                    <PricingItem text="White-label branding" />
                    <PricingItem text="Custom email domain" />
                    <PricingItem text="Unlimited events" />
                    <PricingItem text="Bulk guest management" />
                    <PricingItem text="Dedicated support" />
                    <PricingItem text="SSO & team accounts" />
                    <PricingItem text="SLA guarantee" />
                  </Stack>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    href="mailto:hello@timetorsvp.com"
                    sx={{ py: 1.5, fontSize: '1rem', color: 'text.primary', borderColor: 'divider' }}
                  >
                    Contact Sales
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography
            sx={{
              textAlign: 'center',
              mt: 4,
              color: 'text.secondary',
              fontSize: '0.9rem',
            }}
          >
            Email overage beyond 5 per guest per event: $0.15/guest emailed.
            All plans include email open & click tracking.
          </Typography>
        </Container>
      </Box>

      {/* ─── SOCIAL PROOF / TESTIMONIAL ─── */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: 'linear-gradient(170deg, #5C6BC0 0%, #3949AB 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mb: 3 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <StarIcon key={i} sx={{ color: '#FFD54F', fontSize: 28 }} />
            ))}
          </Stack>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              fontStyle: 'italic',
              lineHeight: 1.7,
              mb: 3,
              px: { xs: 0, md: 4 },
            }}
          >
            "I used to send group texts and hope for the best. With TimeToRSVP I sent
            beautiful invites to 40 guests and knew exactly who was coming — all for
            the price of a coffee."
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>— Sarah K.</Typography>
          <Typography sx={{ opacity: 0.7, fontSize: '0.9rem' }}>
            Hosted a surprise 30th birthday party
          </Typography>
        </Container>
      </Box>

      {/* ─── FAQ ─── */}
      <Box id="faq" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">
          <SectionHeader
            overline="Questions?"
            title="Frequently asked questions"
          />
          <Box sx={{ mt: 4 }}>
            {faqs.map((faq, i) => (
              <Accordion
                key={i}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:not(:last-child)': { borderBottom: 0 },
                  '&::before': { display: 'none' },
                  '&:first-of-type': { borderTopLeftRadius: 12, borderTopRightRadius: 12 },
                  '&:last-of-type': { borderBottomLeftRadius: 12, borderBottomRightRadius: 12 },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600 }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ─── FINAL CTA ─── */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: '#fff',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, mb: 2, letterSpacing: '-0.02em' }}
          >
            Ready to throw the perfect party?
          </Typography>
          <Typography
            sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}
          >
            Create your first event in under a minute. Your guests will love the invites —
            and you'll love knowing exactly who's coming.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            disableElevation
            sx={{
              bgcolor: '#FF7043',
              color: '#fff',
              px: 5,
              py: 1.8,
              fontSize: '1.05rem',
              fontWeight: 700,
              borderRadius: 2,
              '&:hover': { bgcolor: '#E64A19' },
            }}
          >
            Get Started — It's $2
            <ArrowIcon sx={{ ml: 1 }} />
          </Button>
        </Container>
      </Box>

      {/* ─── FOOTER ─── */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#1A1A2E',
          color: 'rgba(255,255,255,0.7)',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: '#fff', mb: 2 }}
              >
                TimeToRSVP
              </Typography>
              <Typography sx={{ lineHeight: 1.7, maxWidth: 300 }}>
                The no-monthly-fee event invite platform. Beautiful emails, one-click
                RSVPs, and a dashboard that shows you who's coming.
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Product
              </Typography>
              <Stack spacing={1}>
                <FooterLink href="#features">Features</FooterLink>
                <FooterLink href="#pricing">Pricing</FooterLink>
                <FooterLink href="#how-it-works">How It Works</FooterLink>
                <FooterLink href="#faq">FAQ</FooterLink>
              </Stack>
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2 }}>
              <Typography sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Company
              </Typography>
              <Stack spacing={1}>
                <FooterLink href="mailto:hello@timetorsvp.com">Contact</FooterLink>
                <FooterLink href="/privacy">Privacy</FooterLink>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography sx={{ fontWeight: 700, color: '#fff', mb: 2 }}>
                Get Started
              </Typography>
              <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
                Create your account and send your first invite in under 30 seconds.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/register')}
                disableElevation
                sx={{
                  bgcolor: '#5C6BC0',
                  '&:hover': { bgcolor: '#3949AB' },
                }}
              >
                Sign Up Free
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography sx={{ fontSize: '0.85rem', opacity: 0.5, textAlign: 'center' }}>
            &copy; {new Date().getFullYear()} TimeToRSVP. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

/* ─── HELPER COMPONENTS ─── */

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Typography
      component="a"
      href={href}
      sx={{
        color: 'text.secondary',
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: '0.95rem',
        transition: 'color 0.15s',
        '&:hover': { color: 'primary.main' },
      }}
    >
      {children}
    </Typography>
  );
}

function TrustStat({ value, label }: { value: string; label: string }) {
  return (
    <Stack alignItems="center" sx={{ flex: 1, py: { xs: 1, sm: 0 } }}>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}
      >
        {value}
      </Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 0.5 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function PricingItem({ text, bold }: { text: string; bold?: boolean }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      <Typography
        variant="body2"
        sx={{ fontWeight: bold ? 700 : 400, color: bold ? 'text.primary' : 'text.secondary' }}
      >
        {text}
      </Typography>
    </Stack>
  );
}

function SectionHeader({
  overline,
  title,
  subtitle,
}: {
  overline: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto' }}>
      <Typography
        variant="overline"
        sx={{ color: 'secondary.main', fontWeight: 700, letterSpacing: 1.5, mb: 1, display: 'block' }}
      >
        {overline}
      </Typography>
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: subtitle ? 2 : 0 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.7 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Typography
      component="a"
      href={href}
      sx={{
        color: 'rgba(255,255,255,0.6)',
        textDecoration: 'none',
        fontSize: '0.9rem',
        transition: 'color 0.15s',
        '&:hover': { color: '#fff' },
      }}
    >
      {children}
    </Typography>
  );
}

/* ─── DATA ─── */

const features = [
  {
    title: 'Designed Email Templates',
    desc: 'Choose from professionally designed templates with curated event images. Invites your guests will actually want to open.',
    icon: <EmailIcon />,
    color: '#5C6BC0',
  },
  {
    title: 'One-Click RSVP',
    desc: 'Guests tap one button to respond — no account, no app, no friction. Yes, No, or Maybe in a single click.',
    icon: <RsvpIcon />,
    color: '#43A047',
  },
  {
    title: 'Calendar Invites',
    desc: 'After RSVPing, guests get a calendar invite they can add to Google, Apple, or Outlook. No more "wait, when was that again?"',
    icon: <CalendarIcon />,
    color: '#1E88E5',
  },
  {
    title: 'Real-Time Dashboard',
    desc: 'See who opened their invite, who clicked, and who RSVP\'d — all updating in real time on your host dashboard.',
    icon: <AnalyticsIcon />,
    color: '#8E24AA',
  },
  {
    title: 'Co-Host Collaboration',
    desc: 'Invite co-hosts who get RSVP notifications and can manage event details alongside you. Perfect for planning together.',
    icon: <PeopleIcon />,
    color: '#F4511E',
  },
  {
    title: 'Email Open & Click Tracking',
    desc: 'Know which guests opened their invite, clicked the RSVP link, and who might need a reminder nudge.',
    icon: <TrackingIcon />,
    color: '#00897B',
  },
  {
    title: 'Gift Exchange',
    desc: 'Built-in Secret Santa and gift exchange assignments. Set budgets, share wishlists, and let TimeToRSVP handle the matching.',
    icon: <GiftIcon />,
    color: '#C62828',
  },
  {
    title: 'Event Check-In',
    desc: 'QR code check-in at the door. See who actually showed up and keep your guest flow smooth.',
    icon: <CheckInIcon />,
    color: '#6D4C41',
  },
  {
    title: 'Guest Groups & CSV Import',
    desc: 'Create reusable guest groups for your recurring crew. Import hundreds of guests from a spreadsheet in seconds.',
    icon: <GroupsIcon />,
    color: '#FF8F00',
  },
  {
    title: 'Custom RSVP Questions',
    desc: 'Ask about dietary restrictions, plus-ones, song requests, or anything else. Collect the info you need at RSVP time.',
    icon: <CustomizeIcon />,
    color: '#5C6BC0',
  },
  {
    title: 'Waitlist Management',
    desc: 'Set a capacity limit and automatically manage a waitlist. Guests get notified when spots open up.',
    icon: <WaitlistIcon />,
    color: '#546E7A',
  },
  {
    title: 'Privacy & Data Controls',
    desc: 'GDPR-ready privacy controls, data retention policies, and guest data export. Your guests\' data stays protected.',
    icon: <PrivacyIcon />,
    color: '#37474F',
  },
  {
    title: 'Webhooks & API',
    desc: 'Connect TimeToRSVP to your workflow with webhooks and a full REST API. Automate guest management your way.',
    icon: <WebhookIcon />,
    color: '#3949AB',
  },
  {
    title: 'Custom Branding',
    desc: 'Add your logo, set brand colors, and use your own domain. Make every invite unmistakably yours.',
    icon: <BrandingIcon />,
    color: '#FF7043',
  },
  {
    title: 'Blazing Fast Setup',
    desc: 'Create an event, add guests, and send invites in under a minute. No learning curve, no onboarding flow.',
    icon: <SpeedIcon />,
    color: '#00ACC1',
  },
];

const eventTypes = [
  'Birthday Parties',
  'Holiday Dinners',
  'Baby Showers',
  'Weddings',
  'Graduation Parties',
  'Housewarming',
  'Game Nights',
  'Team Offsites',
  'Potlucks',
  'Reunions',
  'Retirement Parties',
  'Fundraisers',
  'Book Clubs',
  'Secret Santa',
  'Anniversary Parties',
  'Bridal Showers',
  'Cocktail Parties',
  'BBQs & Cookouts',
];

const faqs = [
  {
    q: 'Do my guests need to create an account?',
    a: 'No. Guests receive an email with a one-click RSVP button. They never need to sign up, download an app, or create a password. It\'s completely frictionless for them.',
  },
  {
    q: 'What does "pay per event" mean?',
    a: 'You pay $2 when you create an event (up to 15 guests). Need more guests? It\'s just $1 per additional 5 guests. There are no monthly subscriptions, no annual fees, and no hidden charges. You only pay when you throw an event.',
  },
  {
    q: 'What happens if I go over the email limit?',
    a: 'Each event includes 5 emails per guest (invite, confirmation, reminders, updates). If you send more than that, it\'s $0.15 per additional guest emailed. Most events never hit this limit.',
  },
  {
    q: 'Can I customize the invite design?',
    a: 'Yes! Choose from our library of professionally designed templates and curated event images. Pro users can upload custom images, set brand colors, and add their own logo for a fully personalized look.',
  },
  {
    q: 'How do co-hosts work?',
    a: 'When you create an event, you can invite co-hosts by email. They\'ll get notified of every RSVP and can edit event details, send updates, and manage the guest list alongside you.',
  },
  {
    q: 'Is there a guest limit?',
    a: 'No hard limit. The base price covers 15 guests, and you can add more at $1 per 5 additional guests. Business plans offer volume pricing for large-scale events.',
  },
  {
    q: 'Do you support gift exchanges?',
    a: 'Yes! TimeToRSVP has built-in Secret Santa and gift exchange support. Set a budget, let guests share wishlists, and we\'ll randomly assign gift partners and notify everyone.',
  },
  {
    q: 'Can I use TimeToRSVP for business events?',
    a: 'Absolutely. Our Business plan includes white-label branding, custom email domains, unlimited events, and team accounts. Contact us for pricing tailored to your organization.',
  },
];
